import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const InstallButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showButton, setShowButton] = useState(false);
  const [showInstructionsPopup, setShowInstructionsPopup] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || (window.navigator as any).standalone 
      || document.referrer.includes('android-app://');

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    console.log('🔍 Install Check:', {
      isStandalone,
      isIOS: iOS,
      displayMode: window.matchMedia('(display-mode: standalone)').matches
    });

    if (isStandalone) {
      console.log('✅ Already installed as PWA');
      return;
    }

    // Check if user dismissed popup before
    const popupDismissed = localStorage.getItem('install-popup-dismissed');

    // Listen for beforeinstallprompt event (Chrome/Edge only)
    const handler = (e: Event) => {
      console.log('🎉 beforeinstallprompt event captured - showing button');
      e.preventDefault();
      setDeferredPrompt(e);
      setShowButton(true); // Show button only for browsers that support native install
    };

    window.addEventListener('beforeinstallprompt', handler);

    // If no event after 8 seconds, show instructions popup for browsers that don't support it
    const timeoutId = setTimeout(() => {
      if (!deferredPrompt && !popupDismissed) {
        console.log('📱 No native install support - showing instructions popup');
        setShowInstructionsPopup(true);
      }
    }, 8000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timeoutId);
    };
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    // Chrome/Edge - use native prompt
    console.log('🚀 Triggering native install prompt');
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('✅ User accepted the install');
      setShowButton(false); // Hide button after installation
    } else {
      console.log('❌ User dismissed the install');
    }
    
    setDeferredPrompt(null);
  };

  const handleDismissPopup = () => {
    setShowInstructionsPopup(false);
    localStorage.setItem('install-popup-dismissed', 'true');
  };

  // Instructions Popup for browsers that don't support native install
  if (showInstructionsPopup) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
        >
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4">
            <button
              onClick={handleDismissPopup}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="flex items-start gap-3 mb-3">
              <div className="bg-red-100 rounded-full p-2 flex-shrink-0">
                <Download className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="font-bold text-gray-900 text-sm mb-2">
                  Install Kiss Coffee App
                </h3>
                {isIOS ? (
                  <div className="text-xs text-gray-700 space-y-1">
                    <p className="flex items-center gap-2">
                      <span className="text-blue-600 text-lg">1️⃣</span>
                      <span>Tap the <strong>Share</strong> button (⬆️) at the bottom</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="text-blue-600 text-lg">2️⃣</span>
                      <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="text-blue-600 text-lg">3️⃣</span>
                      <span>Tap <strong>"Add"</strong> in the top right</span>
                    </p>
                  </div>
                ) : (
                  <div className="text-xs text-gray-700 space-y-1">
                    <p className="flex items-center gap-2">
                      <span className="text-blue-600 text-lg">1️⃣</span>
                      <span>Click the <strong>menu (⋮)</strong> in your browser</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="text-blue-600 text-lg">2️⃣</span>
                      <span>Look for <strong>"Install Kiss Coffee"</strong> or <strong>"Add to Home Screen"</strong></span>
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="text-blue-600 text-lg">3️⃣</span>
                      <span>Click it to install the app</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <button
              onClick={handleDismissPopup}
              className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md transition-colors"
            >
              Got it, thanks!
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Non-iOS: Show install button
  if (!showButton) {
    return null;
  }

  return (
    <button
      onClick={handleInstall}
      className="fixed bottom-4 right-4 z-40 bg-red-600 hover:bg-red-700 text-white pl-3 pr-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 transition-all hover:scale-105 active:scale-95 text-sm font-medium"
      aria-label="Install App"
    >
      <Download className="h-4 w-4" />
      <span className="hidden sm:inline">Install App</span>
      <span className="sm:hidden">Install</span>
    </button>
  );
};

export default InstallButton;

